import CheckIcon from '@material-design-icons/svg/filled/check.svg?react';
import OpenInNewIcon from '@material-design-icons/svg/filled/open_in_new.svg?react';
import { useWallet } from '@solana/wallet-adapter-react';
import { WalletMultiButton } from '@solana/wallet-adapter-react-ui';
import { VersionedTransaction } from '@solana/web3.js';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import tokenSkrIcon from '../../assets/skr-logo.svg';
import { usePageTitle } from '../../hooks/usePageTitle';
import { useTRPC } from '../../hooks/useTRPC';
import { getErrorMessage } from '../../utils/errorHandling';
import { SOLANA_CLUSTER, SOLANA_IS_MAINNET } from '../../utils/solanaUtils';
import { LoadingIcon } from '../LoadingIcon';
import { DashboardHeader } from './DashboardHeader';

const STAKE_URL = 'https://stake.solanamobile.com';

type ClaimState =
  | 'loading'
  | 'not_connected'
  | 'eligible'
  | 'not_eligible'
  | 'already_claimed'
  | 'claiming'
  | 'success'
  | 'error';

interface ClaimData {
  tokenAmount: number;
}

interface ClaimConfig {
  claimEnabled: boolean;
  claimMessage: string | null;
}

const formatTokenAmount = (amount: number) => {
  return amount.toLocaleString();
};

const NetworkChip = () => {
  if (SOLANA_IS_MAINNET) return null;

  return (
    <div className="fixed bottom-4 right-4 px-3 py-1.5 bg-blue-600 text-white text-xs font-medium rounded-lg shadow-lg flex items-center gap-2 z-50">
      <span className="size-2 bg-green-400 rounded-full animate-pulse" />
      <span className="uppercase">{SOLANA_CLUSTER}</span>
    </div>
  );
};

const NotConnectedView = () => {
  return (
    <div className="flex flex-col items-center justify-center flex-1 px-4">
      <div className="flex flex-col items-center max-w-md">
        <div className="mb-6">
          <img
            src={tokenSkrIcon}
            alt="SKR Token"
            className="size-24 drop-shadow-[0_8px_24px_rgba(0,0,0,0.5)]"
          />
        </div>

        <h1 className="text-2xl font-bold text-white mb-2">Claim Your SKR</h1>
        <p className="text-neutral-400 text-center">
          Connect your wallet to check your eligibility for the SKR airdrop.
        </p>
      </div>
    </div>
  );
};

const LoadingView = () => {
  return (
    <div className="flex flex-col items-center justify-center flex-1 px-4">
      <div className="flex flex-col items-center max-w-md">
        <div className="mb-6">
          <img
            src={tokenSkrIcon}
            alt="SKR Token"
            className="size-24 drop-shadow-[0_8px_24px_rgba(0,0,0,0.5)]"
          />
        </div>

        <h1 className="text-2xl font-bold text-white mb-4">
          Checking Eligibility
        </h1>
        <LoadingIcon
          label="Checking eligibility"
          className="size-6 text-white"
        />
      </div>
    </div>
  );
};

const NotEligibleView = () => {
  return (
    <div className="flex flex-col items-center justify-center flex-1 px-4">
      <div className="flex flex-col items-center max-w-md">
        <div className="mb-6">
          <img
            src={tokenSkrIcon}
            alt="SKR Token"
            className="size-24 drop-shadow-[0_8px_24px_rgba(0,0,0,0.5)] grayscale opacity-50"
          />
        </div>

        <h1 className="text-2xl font-bold text-white mb-2">Not Eligible</h1>
        <p className="text-neutral-400 text-center mb-8">
          This wallet is not eligible for the SKR airdrop. Please connect a
          different wallet if you believe you should be eligible.
        </p>

        <WalletMultiButton />
      </div>
    </div>
  );
};

const AlreadyClaimedView = ({ tokenAmount }: { tokenAmount: number }) => {
  return (
    <div className="flex flex-col items-center justify-center flex-1 px-4">
      <div className="flex flex-col items-center max-w-md">
        <div className="mb-6">
          <img
            src={tokenSkrIcon}
            alt="SKR Token"
            className="size-24 drop-shadow-[0_8px_24px_rgba(0,0,0,0.5)]"
          />
        </div>

        <h1 className="text-2xl font-bold text-white mb-2">Already Claimed</h1>
        <p className="text-neutral-400 text-center">
          You have already claimed {formatTokenAmount(tokenAmount)} SKR with
          this wallet.
        </p>
      </div>
    </div>
  );
};

const ErrorView = ({
  error,
  onRetry,
}: {
  error: string;
  onRetry: () => void;
}) => {
  return (
    <div className="flex flex-col items-center justify-center flex-1 px-4">
      <div className="flex flex-col items-center max-w-md">
        <div className="mb-6">
          <img
            src={tokenSkrIcon}
            alt="SKR Token"
            className="size-24 drop-shadow-[0_8px_24px_rgba(0,0,0,0.5)]"
          />
        </div>

        <h1 className="text-2xl font-bold text-white mb-2">
          Something Went Wrong
        </h1>
        <p className="text-red-400 text-center mb-8">{error}</p>

        <button
          type="button"
          onClick={onRetry}
          className="py-3 px-6 bg-gradient-to-r from-teal-600 to-teal-500 hover:from-teal-500 hover:to-teal-400 text-white font-semibold rounded-lg transition-all duration-200"
        >
          Try Again
        </button>
      </div>
    </div>
  );
};

const ClaimView = ({
  claimData,
  claimConfig,
  onClaim,
  isClaiming,
}: {
  claimData: ClaimData;
  claimConfig: ClaimConfig;
  onClaim: () => void;
  isClaiming: boolean;
}) => {
  const isDisabled = !claimConfig.claimEnabled || isClaiming;

  return (
    <div className="flex flex-col items-center justify-center flex-1 px-4">
      <div className="flex flex-col items-center max-w-md">
        <div className="mb-6">
          <img
            src={tokenSkrIcon}
            alt="SKR Token"
            className="size-24 drop-shadow-[0_8px_24px_rgba(0,0,0,0.5)]"
          />
        </div>

        <h1 className="text-2xl font-bold text-white mb-2">Claim Your SKR</h1>
        <p className="text-neutral-400 text-center mb-8">
          As a dedicated Solana Mobile publisher, we appreciate your work and
          are rewarding your contributions.
        </p>

        <div className="w-full bg-neutral-800/50 border border-neutral-700 rounded-xl p-6">
          <div className="text-center">
            <span className="text-neutral-400 text-sm">Allocation</span>
            <div className="text-4xl font-bold text-white my-2">
              {formatTokenAmount(claimData.tokenAmount)} SKR
            </div>

            {claimConfig.claimEnabled && (
              <p className="text-neutral-400 text-sm mt-4 px-2">
                By clicking below, you acknowledge Solana Mobile's{' '}
                <a
                  href="https://solanamobile.com/privacy-policy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline hover:text-neutral-300"
                >
                  Privacy Policy
                </a>{' '}
                and agree to the{' '}
                <a
                  href="https://legal.solanamobile.com/en/skr-tos"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline hover:text-neutral-300"
                >
                  Seeker Token Airdrop Terms
                </a>
                , including{' '}
                <a
                  href="https://legal.solanamobile.com/en/skr-tos#disputes"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline hover:text-neutral-300"
                >
                  BINDING ARBITRATION
                </a>{' '}
                on an individual, non-class basis.
              </p>
            )}
          </div>

          <div className="mt-6 bg-black/40 rounded-xl p-4">
            <button
              type="button"
              onClick={onClaim}
              disabled={isDisabled}
              style={{ cursor: isDisabled ? 'not-allowed' : 'pointer' }}
              className="w-full py-3 px-4 bg-gradient-to-r from-teal-600 to-teal-500 hover:from-teal-500 hover:to-teal-400 disabled:from-neutral-700 disabled:to-neutral-700 text-white disabled:text-neutral-400 font-semibold rounded-lg transition-all duration-200 flex items-center justify-center gap-2"
            >
              {isClaiming ? (
                <>
                  <LoadingIcon label="Claiming" className="size-6 text-white" />
                  <span>Claiming…</span>
                </>
              ) : (
                'Claim SKR'
              )}
            </button>

            {claimConfig.claimMessage && (
              <p className="text-neutral-400 text-center mt-4">
                {claimConfig.claimMessage}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const ClaimSuccessView = ({
  tokenAmount,
  walletAddress,
  txSignature,
}: {
  tokenAmount: number;
  walletAddress: string;
  txSignature: string;
}) => {
  const solscanBaseUrl = SOLANA_IS_MAINNET
    ? 'https://solscan.io'
    : 'https://solscan.io?cluster=devnet';

  return (
    <div className="flex flex-col items-center justify-center flex-1 px-4 py-8">
      <div className="flex flex-col items-center max-w-lg w-full">
        <div className="size-20 bg-emerald-400 rounded-[20px] flex items-center justify-center mb-8">
          <CheckIcon className="size-12 fill-emerald-900" />
        </div>

        <p className="text-4xl font-normal text-white text-center leading-none tracking-tight m-0">
          You have claimed
        </p>
        <p className="text-4xl font-normal text-white mb-6 text-center leading-none tracking-tight m-0">
          {formatTokenAmount(tokenAmount)} SKR!
        </p>

        <p className="text-neutral-400 text-center text-sm leading-relaxed">
          Your SKR tokens can be found in the following wallet:
        </p>
        <p className="text-neutral-400 text-center text-sm mb-6 font-mono">
          {walletAddress.slice(0, 8)}...{walletAddress.slice(-8)}
        </p>

        <a
          href={`${solscanBaseUrl}/tx/${txSignature}`}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-1.5 text-neutral-500 hover:text-neutral-300 text-sm transition-colors mb-10"
        >
          View Transaction on Solscan
          <OpenInNewIcon className="size-4 fill-current" />
        </a>

        <div
          className="w-full rounded-2xl p-10 overflow-hidden relative"
          style={{
            backgroundColor: '#1D2323',
          }}
        >
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background: `
                radial-gradient(ellipse 120% 40% at 70% 100%, rgba(7, 112, 159, 0.6), transparent 60%),
                radial-gradient(ellipse 80% 25% at 70% 105%, rgba(18, 154, 139, 0.7), transparent 50%),
                radial-gradient(ellipse 60% 15% at 70% 110%, rgba(98, 243, 198, 0.5), transparent 40%),
                radial-gradient(ellipse 50% 10% at 70% 115%, rgba(246, 246, 245, 0.3), transparent 30%)
              `,
            }}
          />

          <div className="relative z-10">
            <h3 className="text-4xl font-normal text-[#95d2e6] leading-none tracking-tight">
              Stake SKR &amp;
            </h3>
            <h3 className="text-4xl font-normal leading-none tracking-tight mb-6">
              <span className="text-[#95d2e6]">Earn up to </span>
              <span className="text-[#faf9f4] [text-shadow:0_0_40px_rgba(250,249,244,0.5)]">
                25% APY
              </span>
            </h3>

            <p className="text-white text-lg leading-relaxed mb-10">
              Stake instantly, delegate to Guardians, support the Solana Mobile
              ecosystem, and earn rewards. Unstaking takes 48 hours.
            </p>

            <a
              href={STAKE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 w-full py-4 text-black font-medium rounded-xl transition-all duration-200 hover:opacity-90"
              style={{ backgroundColor: 'white' }}
            >
              Stake Now
              <OpenInNewIcon className="size-5 fill-current" />
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export const ClaimSkrPage = () => {
  usePageTitle('Claim SKR | Publishing Portal');

  const trpc = useTRPC();
  const { connected, publicKey, signTransaction } = useWallet();
  const [claimState, setClaimState] = useState<ClaimState>('not_connected');
  const [claimData, setClaimData] = useState<ClaimData | null>(null);
  const [claimConfig, setClaimConfig] = useState<ClaimConfig>({
    claimEnabled: false,
    claimMessage: null,
  });
  const [error, setError] = useState<string | null>(null);
  const [txSignature, setTxSignature] = useState<string | null>(null);
  const [retryKey, setRetryKey] = useState(0);

  const walletAddress = publicKey?.toBase58() ?? null;

  const handleRetry = () => {
    setRetryKey((k) => k + 1);
  };

  useEffect(() => {
    if (!connected || !walletAddress) {
      setClaimState('not_connected');
      setClaimData(null);
      setError(null);
      return;
    }

    let cancelled = false;

    const checkEligibility = async () => {
      setClaimState('loading');
      setError(null);

      try {
        const [configResult, eligibilityResult, claimStatusResult] =
          await Promise.all([
            trpc.skrAirdrop.getClaimConfig.query(undefined),
            trpc.skrAirdrop.checkWalletEligibility.query({
              walletAddress,
            }),
            trpc.skrAirdrop.getSkrClaimStatus.query({
              walletAddress,
              cluster: SOLANA_CLUSTER,
            }),
          ]);

        if (cancelled) return;

        setClaimConfig(configResult);

        if (claimStatusResult.claimed) {
          setClaimData({ tokenAmount: eligibilityResult.tokenAmount });
          setClaimState('already_claimed');
        } else if (eligibilityResult.eligible) {
          setClaimData({ tokenAmount: eligibilityResult.tokenAmount });
          setClaimState('eligible');
        } else {
          setClaimState('not_eligible');
        }
      } catch (err) {
        if (cancelled) return;
        const message = getErrorMessage(err, 'Failed to check eligibility');
        setError(message);
        setClaimState('error');
      }
    };

    checkEligibility();

    return () => {
      cancelled = true;
    };
  }, [connected, walletAddress, trpc, retryKey]);

  const handleClaim = async () => {
    if (!connected || !publicKey || !signTransaction) {
      toast.error('Please connect your wallet first');
      return;
    }

    setClaimState('claiming');
    setError(null);

    try {
      toast.info('Fetching claim transaction...');

      const claimTxResult = await trpc.skrAirdrop.getClaimTx.query({
        claimant: publicKey.toBase58(),
        stake: 0,
      });

      if (!claimTxResult.tx || claimTxResult.tx.length === 0) {
        throw new Error('No transaction returned from claim API');
      }

      toast.info('Please sign the transaction in your wallet...');

      const txBuffer = Buffer.from(claimTxResult.tx[0], 'base64');
      const transaction = VersionedTransaction.deserialize(txBuffer);

      const signedTx = await signTransaction(transaction);

      toast.info('Submitting transaction...');

      const submitResult = await trpc.submitNftTransaction.mutate({
        signedTransaction: Buffer.from(signedTx.serialize()).toString('base64'),
      });

      if (submitResult._tag === 'Left') {
        throw new Error(submitResult.left.message);
      }

      const txSignatureResult = submitResult.right.transactionSignature;
      setTxSignature(txSignatureResult);

      await trpc.skrAirdrop.recordSkrClaim.mutate({
        walletAddress: publicKey.toBase58(),
        cluster: SOLANA_CLUSTER,
        transactionHash: txSignatureResult,
        tokenAmount: claimData?.tokenAmount ?? 0,
      });

      setClaimState('success');
      toast.success('SKR claimed successfully!');
    } catch (err) {
      const message = getErrorMessage(err, 'Failed to claim SKR');

      const isUserRejection =
        message.toLowerCase().includes('user rejected') ||
        message.toLowerCase().includes('user denied') ||
        message.toLowerCase().includes('user cancelled');

      if (isUserRejection) {
        setClaimState('eligible');
        toast.info('Transaction cancelled');
      } else {
        setError(message);
        setClaimState('error');
        toast.error(message);
      }
    }
  };

  const renderContent = () => {
    switch (claimState) {
      case 'not_connected':
        return <NotConnectedView />;
      case 'loading':
        return <LoadingView />;
      case 'not_eligible':
        return <NotEligibleView />;
      case 'already_claimed':
        return <AlreadyClaimedView tokenAmount={claimData?.tokenAmount ?? 0} />;
      case 'error':
        return (
          <ErrorView error={error || 'Unknown error'} onRetry={handleRetry} />
        );
      case 'eligible':
      case 'claiming':
        return claimData ? (
          <ClaimView
            claimData={claimData}
            claimConfig={claimConfig}
            onClaim={handleClaim}
            isClaiming={claimState === 'claiming'}
          />
        ) : null;
      case 'success':
        return claimData && publicKey && txSignature ? (
          <ClaimSuccessView
            tokenAmount={claimData.tokenAmount}
            walletAddress={publicKey.toBase58()}
            txSignature={txSignature}
          />
        ) : null;
      default:
        return null;
    }
  };

  return (
    <div className="flex flex-col h-full">
      <DashboardHeader
        icon={
          <img src={tokenSkrIcon} alt="SKR" className="size-4 rounded-full" />
        }
        title="Claim Your SKR"
      />

      <div className="flex-1 bg-[#161717] rounded-b-xl overflow-auto flex">
        {renderContent()}
      </div>

      <NetworkChip />
    </div>
  );
};
